import React, { createContext, useContext, useState, useEffect } from 'react';
import { hasPermission } from '../permissionUtils';

/**
 * Block Permission Context
 * 
 * This context provides block-level permission checking for components.
 * Block permissions control which accordion sections (What is it?, Test it, Check it, Try it, Quiz)
 * are visible to users based on their role.
 * 
 * Permission codes:
 * - _w = "What is it?" block
 * - _t = "Test it" block
 * - _k = "Check it" block
 * - _y = "Try it" block
 * - _q = "Quiz" block
 */

const BlockPermissionContext = createContext();

export const BlockPermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    // Load permissions from localStorage
    const loadPermissions = () => {
      const storedPermissions = localStorage.getItem("route_access");
      if (storedPermissions) {
        try {
          setPermissions(JSON.parse(storedPermissions));
        } catch (e) {
          console.error("Error parsing route_access:", e);
          setPermissions([]);
        }
      }
    };

    loadPermissions();

    // Listen for storage changes (in case permissions are updated)
    window.addEventListener('storage', loadPermissions);
    return () => window.removeEventListener('storage', loadPermissions);
  }, []);

  /**
   * Check if a specific block is permitted for a given page index
   * @param {string} pageIndex - The page permission index (e.g., "3_0" for What is NLP)
   * @param {string} blockType - The block type: 'w' (What is it), 't' (Test it), 'k' (Check it), 'y' (Try it), 'q' (Quiz)
   * @returns {boolean} - Whether the block is permitted
   */
  const hasBlockPermission = (pageIndex, blockType) => {
    return hasPermission(permissions, `${pageIndex}_${blockType}`);
  };

  /**
   * Check multiple block permissions at once
   * @param {string} pageIndex - The page permission index
   * @returns {object} - Object with boolean values for each block type
   * 
   * Note: If a user has view access (_v) or create/edit access (_c) to a page, all blocks are shown by default.
   * Individual block permissions (_w, _t, _k, _y, _q) can be used for granular control
   * when view/create access is not granted but specific blocks should be visible.
   */
  const getBlockPermissions = (pageIndex) => {
    /* Added the following code for 000.harsha using AI Agent. 10 Jan 2026. Code Start */
    // If user has view access (_v) OR create/edit access (_c) to the page, show all blocks by default
    const hasViewAccess = hasPermission(permissions, `${pageIndex}_v`);
    const hasCreateAccess = hasPermission(permissions, `${pageIndex}_c`);
    const hasFullAccess = hasViewAccess || hasCreateAccess;
    /* Code end. harsha using AI Agent. 10 Jan 2026 */
    
    return {
      whatIsIt: hasFullAccess || hasPermission(permissions, `${pageIndex}_w`),
      testIt: hasFullAccess || hasPermission(permissions, `${pageIndex}_t`),
      checkIt: hasFullAccess || hasPermission(permissions, `${pageIndex}_k`),
      tryIt: hasFullAccess || hasPermission(permissions, `${pageIndex}_y`),
      quiz: hasFullAccess || hasPermission(permissions, `${pageIndex}_q`),
    };
  };

  /**
   * Refresh permissions from localStorage
   */
  const refreshPermissions = () => {
    const storedPermissions = localStorage.getItem("route_access");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Error parsing route_access:", e);
      }
    }
  };

  return (
    <BlockPermissionContext.Provider value={{ 
      permissions, 
      hasBlockPermission, 
      getBlockPermissions,
      refreshPermissions 
    }}>
      {children}
    </BlockPermissionContext.Provider>
  );
};

/**
 * Hook to use block permissions in components
 * @returns {object} - { permissions, hasBlockPermission, getBlockPermissions, refreshPermissions }
 */
export const useBlockPermissions = () => {
  const context = useContext(BlockPermissionContext);
  if (!context) {
    throw new Error('useBlockPermissions must be used within a BlockPermissionProvider');
  }
  return context;
};

export default BlockPermissionContext;





